import apiClient from "./client";

/**
 * Stripe subscription endpoints (backend apps/payments, ticket #181).
 *
 * Card entry is entirely Stripe-hosted: `startCheckout` / `openBillingPortal`
 * return a Stripe URL the browser redirects to, so no PAN ever touches this app.
 * `getSubscriptionStatus` is the single source the trial banner + paywall read.
 */

export type SubscriptionStatus =
  | "none"
  | "incomplete"
  | "incomplete_expired"
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "unpaid"
  | "paused";

export type SubscriptionPlan = "monthly" | "yearly" | "";

export interface SubscriptionState {
  status: SubscriptionStatus;
  plan: SubscriptionPlan;
  trial_end: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  /** Billing truth only: a trialing/active/past_due Stripe subscription. */
  is_entitled: boolean;
  /** Demo persona or reviewer allowlist: let through without paying. Not a
   *  subscription, so /payment still shows the plans (DLT-W-201). */
  is_exempt: boolean;
  gating_enabled: boolean;
  /** THE trial length, STRIPE_TRIAL_PERIOD_DAYS (contract batch 10b). Optional
   *  for an older backend; readers fall back to lib/trial's TRIAL_DAYS. */
  trial_period_days?: number;
}

export async function getSubscriptionStatus(): Promise<SubscriptionState> {
  const { data } = await apiClient.get<SubscriptionState>("/payments/status/");
  return data;
}

export async function startCheckout(plan: "monthly" | "yearly"): Promise<string> {
  const { data } = await apiClient.post<{ url: string }>("/payments/checkout/", { plan });
  return data.url;
}

/** AN-29: the same Checkout, embedded in the page. Returns the client secret Stripe.js mounts. */
export async function startEmbeddedCheckout(plan: "monthly" | "yearly"): Promise<string> {
  const { data } = await apiClient.post<{ client_secret: string }>("/payments/checkout/embedded/", {
    plan,
  });
  return data.client_secret;
}

export async function openBillingPortal(): Promise<string> {
  const { data } = await apiClient.post<{ url: string }>("/payments/portal/", {});
  return data.url;
}
